import CircleIcon from '@mui/icons-material/Circle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Badge, Box, CircularProgress, ListItem, Menu, Skeleton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { CustomerUserDetails, UserNotificationList, UserNotificationText } from '../../backendsdk';
import {
    TrackedIconButton as IconButton,
    TrackedListItemButton as ListItemButton,
} from '../../components/TrackedComponents';
import useApi from '../../hooks/api';
import useCallbackRef from '../../hooks/callbackRef';
import useIsMobile from '../../hooks/isMobile';
import { getTimeAgo } from '../../utils/TimeFormatter';
import palette from '../ColorPalette';
import { Portal } from '../content/Accident/MentionsTextField';
import { getUserAvatar } from '../content/Coaching/SessionDetails';
import { RTL_LANGUAGES } from './Navigator';

interface NotificationsComponentProps {
    initialNotifications?: UserNotificationList;
    setInitialNotifications: CallableFunction;
    isOpen: boolean;
    setIsOpen: CallableFunction;
    onOpen: CallableFunction;
    customerUsers: CustomerUserDetails[];
}

const NotificationsComponent: React.FC<NotificationsComponentProps> = (props: NotificationsComponentProps) => {
    const [notifications, setNotifications] = useState<UserNotificationList>();
    const nextNotificationIdRef = useRef<number | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { i18n, t } = useTranslation();
    const { api } = useApi();
    const [containerRef, containerNode] = useCallbackRef<HTMLDivElement>();
    const [targetRef, targetNode] = useCallbackRef<HTMLLIElement>();
    const currentLanguage = i18n.languages[0];
    const history = useHistory();
    const isMobile = useIsMobile();

    useEffect(() => {
        nextNotificationIdRef.current = notifications?.next_notification_id || null;
    }, [notifications]);

    useEffect(() => {
        if (!!props.initialNotifications && (!notifications || !props.isOpen)) {
            setNotifications(props.initialNotifications);
            setIsLoading(false);
        }
    }, [props.initialNotifications]);

    const getMoreNotifications = (nextNotificationId: number | null) => {
        api.apiV0UserNotificationGet({
            from: nextNotificationId || undefined,
        }).then((res) => {
            setNotifications((prev) => {
                if (!prev) {
                    return res.data;
                }
                return {
                    ...prev,
                    notifications: [...prev.notifications, ...res.data.notifications],
                    next_notification_id: res.data.next_notification_id,
                };
            });
        });
    };

    useEffect(() => {
        if (!!containerNode && !!targetNode) {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            getMoreNotifications(nextNotificationIdRef.current);
                        }
                    });
                },
                {
                    root: containerNode,
                    threshold: 0.5,
                },
            );

            if (targetNode) {
                observer.observe(targetNode);
            }

            return () => {
                if (targetNode) {
                    observer.unobserve(targetNode);
                }
                observer.disconnect();
            };
        }
    }, [containerNode, targetNode]);

    let notificationsContent = <NotificationsIcon sx={{ color: isMobile ? palette.primary : palette.white }} />;
    if (!!props.initialNotifications?.new_count) {
        notificationsContent = (
            <Badge badgeContent={props.initialNotifications.new_count} max={9} color={isMobile ? 'error' : 'secondary'}>
                {notificationsContent}
            </Badge>
        );
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        props.setIsOpen(true);
        props.onOpen();
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        props.setIsOpen(false);
    };

    let content;
    if (isLoading) {
        content = (
            <Box
                sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <CircularProgress />
            </Box>
        );
    } else if (!!notifications && notifications.notifications.length === 0) {
        content = (
            <Box
                sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <Typography variant="overline">{t('content.notifications.empty')}</Typography>
            </Box>
        );
    } else if (!!notifications) {
        content = (
            <>
                {notifications.notifications.map((n) => {
                    const relatedUser = props.customerUsers.find((u) => u.user_id === n.related_user);
                    return (
                        <ListItem
                            disablePadding
                            disableGutters
                            key={n.id}
                            id={`notification-${n.id}`}
                            sx={{ width: '100%', borderBottom: '1px solid #cfcfcf' }}
                        >
                            <ListItemButton
                                id={`notification-${n.id}-button`}
                                data-testid={`notification-${n.id}-button`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!n.is_read) {
                                        api.apiV0UserNotificationNotificationIdPatch({
                                            notificationId: n.id,
                                            userNotificationRequest: { is_read: !n.is_read },
                                        });
                                    }
                                    history.push(n.link);
                                    props.setInitialNotifications((prev: UserNotificationList | undefined) => {
                                        if (prev) {
                                            return {
                                                ...prev,
                                                notifications: prev.notifications.map((notification) => {
                                                    if (notification.id === n.id) {
                                                        return {
                                                            ...notification,
                                                            is_read: !n.is_read,
                                                        };
                                                    }
                                                    return notification;
                                                }),
                                            };
                                        }
                                        return prev;
                                    });
                                    handleMenuClose();
                                }}
                                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {!!relatedUser ? getUserAvatar(relatedUser, 40) : null}
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ fontSize: 12, color: palette.neutral[500] }}>
                                            {getTimeAgo(dayjs.unix(n.created_at), dayjs(), t)}
                                        </Typography>
                                        <Typography>{n.text[currentLanguage as keyof UserNotificationText]}</Typography>
                                    </Box>
                                </Box>
                                <IconButton
                                    id={`btn-notification-${n.id}-${n.is_read ? 'un' : ''}read`}
                                    data-testid={`btn-notification-${n.id}-${n.is_read ? 'un' : ''}read`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setNotifications((prev) => {
                                            if (prev) {
                                                return {
                                                    ...prev,
                                                    notifications: prev.notifications.map((notification) => {
                                                        if (notification.id === n.id) {
                                                            return {
                                                                ...notification,
                                                                is_read: !n.is_read,
                                                            };
                                                        }
                                                        return notification;
                                                    }),
                                                };
                                            }
                                            return prev;
                                        });
                                        props.setInitialNotifications((prev: UserNotificationList | undefined) => {
                                            if (prev) {
                                                return {
                                                    ...prev,
                                                    notifications: prev.notifications.map((notification) => {
                                                        if (notification.id === n.id) {
                                                            return {
                                                                ...notification,
                                                                is_read: !n.is_read,
                                                            };
                                                        }
                                                        return notification;
                                                    }),
                                                };
                                            }
                                            return prev;
                                        });
                                        api.apiV0UserNotificationNotificationIdPatch({
                                            notificationId: n.id,
                                            userNotificationRequest: { is_read: !n.is_read },
                                        }).catch(() => {
                                            setNotifications((prev) => {
                                                if (prev) {
                                                    return {
                                                        ...prev,
                                                        new_count: prev.new_count - 1,
                                                        notifications: prev.notifications.map((notification) => {
                                                            if (notification.id === n.id) {
                                                                return {
                                                                    ...notification,
                                                                    is_read: n.is_read,
                                                                };
                                                            }
                                                            return notification;
                                                        }),
                                                    };
                                                }
                                                return prev;
                                            });
                                            props.setInitialNotifications((prev: UserNotificationList | undefined) => {
                                                if (prev) {
                                                    return {
                                                        ...prev,
                                                        new_count: prev.new_count - 1,
                                                        notifications: prev.notifications.map((notification) => {
                                                            if (notification.id === n.id) {
                                                                return {
                                                                    ...notification,
                                                                    is_read: !n.is_read,
                                                                };
                                                            }
                                                            return notification;
                                                        }),
                                                    };
                                                }
                                                return prev;
                                            });
                                        });
                                    }}
                                >
                                    <CircleIcon
                                        sx={{ fontSize: 12, color: n.is_read ? 'transparent' : palette.secondary }}
                                    />
                                </IconButton>
                            </ListItemButton>
                        </ListItem>
                    );
                })}
                {!!notifications.next_notification_id ? (
                    <ListItem ref={targetRef} sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                        <Skeleton
                            variant="circular"
                            animation="wave"
                            width={40}
                            height={40}
                            sx={{ flexShrink: 0, mr: 1 }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <Skeleton animation="wave" width="100%" />
                            <Skeleton animation="wave" width="100%" />
                        </Box>
                    </ListItem>
                ) : null}
            </>
        );
    }

    if (isMobile) {
        content = props.isOpen ? (
            <Portal>
                <Box
                    ref={containerRef}
                    sx={{
                        position: 'absolute',
                        top: 56,
                        left: 0,
                        width: '100%',
                        height: 'calc(100% - 56px)',
                        backgroundColor: palette.neutral[50],
                        zIndex: 2000,
                    }}
                >
                    {content}
                </Box>
            </Portal>
        ) : null;
    } else {
        content = (
            <Menu
                anchorEl={anchorEl}
                id="notifications-menu"
                data-testid="notifications-menu"
                open={props.isOpen}
                onClose={handleMenuClose}
                onClick={handleMenuClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        backgroundColor: palette.neutral[50],
                        width: '350px',
                        height: '400px',
                        ml: '20px',
                        '&::before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 15,
                            left: -5,
                            width: 10,
                            height: 10,
                            bgcolor: palette.neutral[50],
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
                transformOrigin={{
                    horizontal: RTL_LANGUAGES.includes(currentLanguage) ? 'right' : 'left',
                    vertical: 'bottom',
                }}
                anchorOrigin={{
                    horizontal: RTL_LANGUAGES.includes(currentLanguage) ? 'left' : 'right',
                    vertical: 'top',
                }}
                MenuListProps={{ disablePadding: true }}
            >
                <Box ref={containerRef} sx={{ width: '350px', height: '400px', overflow: 'auto' }}>
                    {content}
                </Box>
            </Menu>
        );
    }

    return (
        <>
            <IconButton
                id="btn-notifications"
                data-testid="btn-notifications"
                data-new-notifications={!!props.initialNotifications ? props.initialNotifications.new_count : 0}
                onClick={(e) => {
                    if (props.isOpen) {
                        handleMenuClose();
                    } else {
                        handleMenuOpen(e);
                    }
                }}
                className={!!props.initialNotifications?.new_count ? 'pulse' : ''}
            >
                {notificationsContent}
            </IconButton>
            {content}
        </>
    );
};

export default NotificationsComponent;
